import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { MdDownload, MdLocalShipping, MdDescription, MdCardGiftcard, MdEvent, MdComputer, MdAccountBalanceWallet, MdTrendingUp, MdCheckCircle } from 'react-icons/md';
import api from '../services/api';
import { getCurrentUser } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import ClientLayout from '../components/ClientLayout';

const BRAND = '#068BC9';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const SERVICE_COLORS = ['#068BC9', '#8b5cf6', '#22c55e', '#f97316', '#ef4444'];
const INDIAN_CITIES = ['All Cities','Mumbai','Delhi','Bangalore','Hyderabad','Chennai','Kolkata','Pune','Ahmedabad','Jaipur','Surat','Lucknow','Kanpur','Nagpur','Indore','Bhopal','Patna','Vadodara','Ghaziabad','Ludhiana','Agra','Nashik','Faridabad','Meerut','Rajkot','Varanasi','Srinagar','Aurangabad','Dhanbad','Amritsar','Allahabad','Ranchi','Howrah','Coimbatore','Jabalpur','Gwalior','Vijayawada','Jodhpur','Madurai','Raipur','Kota','Guwahati','Chandigarh','Solapur','Hubli','Thiruvananthapuram','Mysuru','Tiruppur','Gurgaon','Noida','Secunderabad'];

function formatLakh(val) {
  if (val >= 100000) return String.fromCharCode(8377) + (val/100000).toFixed(1) + 'L';
  if (val >= 1000) return String.fromCharCode(8377) + (val/1000).toFixed(1) + 'K';
  return String.fromCharCode(8377) + parseFloat(val || 0).toFixed(0);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</p>
        <p style={{ fontSize: 14, fontWeight: 700, color: BRAND }}>{String.fromCharCode(8377)}{payload[0].value.toFixed(2)}L</p>
      </div>
    );
  }
  return null;
};

export default function RapidoAnalytics() {
  const user = getCurrentUser();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [invoices, setInvoices] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [cgOrders, setCgOrders] = useState([]);
  const [selectedCity, setSelectedCity] = useState('All Cities');
  const [companyCity, setCompanyCity] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showCityPopup, setShowCityPopup] = useState(false);

  useEffect(() => {
    if (!user?.companyId) return;
    Promise.all([
      api.get(`/api/analytics/overview/${user.companyId}`),
      api.get(`/api/company-invoices/company/${user.companyId}`),
      api.get(`/api/corporate-giftings/company/${user.companyId}`).catch(() => null),
      api.get(`/api/wallet/balance/${user.companyId}`).catch(() => null),
      api.get(`/api/companies/${user.companyId}`).catch(() => null),
    ]).then(([analyticsRes, invoicesRes, cgRes, walletRes, companyRes]) => {
      setData(analyticsRes.data);
      setInvoices(invoicesRes.data);
      if (cgRes) setCgOrders(cgRes.data || []);
      if (walletRes) setWalletData(walletRes.data);
      if (companyRes) {
        const city = companyRes.data?.city || '';
        const name = companyRes.data?.name || '';
        setCompanyCity(city);
        setCompanyName(name);
        localStorage.setItem('companyCity', city);
        localStorage.setItem('companyName', name);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.companyId]);

  const cityMatches = selectedCity === "All Cities" || !companyCity || selectedCity === companyCity;

  const monthlyData = (cityMatches ? data?.monthlyTrend : [])?.map(m => ({
    name: MONTHS[(parseInt(m.month) || 1) - 1],
    spend: parseFloat(m.spend || 0) / 100000
  })) || [];

  const totalSpend = cityMatches ? (data?.totalSpend || 0) : 0;
  const serviceSpend = cityMatches ? (data?.serviceSpend || []) : [];
  const totalServiceSpend = serviceSpend.reduce((a, b) => a + parseFloat(b.spend || 0), 0);

  const SERVICES = [
    { label: 'Logistics', icon: <MdLocalShipping size={22}/>, path: '/client/logistics', color: BRAND, key: 'Logistics' },
    { label: 'Stamp Paper', icon: <MdDescription size={22}/>, path: '/stamp-paper', color: '#8b5cf6', key: 'Stamp Paper' },
    { label: 'Corporate Gifting', icon: <MdCardGiftcard size={22}/>, path: '/gifting', color: '#22c55e', key: 'Corporate Gifting' },
    { label: 'Event & Team Outing', icon: <MdEvent size={22}/>, path: '/events', color: '#f97316', key: 'Events' },
    { label: 'IT Solutions', icon: <MdComputer size={22}/>, path: '/it-solutions', color: '#ef4444', key: 'IT Solutions' },
  ];

  const statCards = [
    { label: 'Total Expenses', value: formatLakh(totalSpend), sub: 'FY 2026-27', icon: <MdAccountBalanceWallet size={20}/>, color: BRAND, bg: '#e0f2fe' },
    { label: 'Logistics Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Logistics')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Logistics')?.requests||0} shipments`, icon: <MdLocalShipping size={20}/>, color: '#068BC9', bg: '#e0f2fe' },
    { label: 'Corporate Gifting Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Corporate Gifting')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Corporate Gifting')?.requests||0} orders`, icon: <MdCardGiftcard size={20}/>, color: '#22c55e', bg: '#dcfce7' },
    { label: 'Stamp Paper Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Stamp Paper')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Stamp Paper')?.requests||0} orders`, icon: <MdDescription size={20}/>, color: '#8b5cf6', bg: '#ede9fe' },
    { label: 'Event Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Events')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Events')?.requests||0} events`, icon: <MdEvent size={20}/>, color: '#f97316', bg: '#ffedd5' },
    { label: 'IT Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='IT Solutions')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='IT Solutions')?.requests||0} tickets`, icon: <MdComputer size={20}/>, color: '#ef4444', bg: '#fee2e2' },
  ];

  const reportTabMap = { 'Logistics': 'logistics', 'Stamp Paper': 'stamp', 'Corporate Gifting': 'gifting', 'Events': 'events', 'IT Solutions': 'it' };

  const now = new Date();
  const thisMonth = now.toLocaleString('default', { month: 'short' });
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleString('default', { month: 'short' });
  const thisYear = now.getFullYear();
  const lastYear = thisYear - 1;

  return (
    <ClientLayout>
      <div className="p-6 space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Pan-India Analytics</h1>
          <p className="text-xs text-gray-400">Consolidated expense and service overview</p>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {['overview', 'reports', 'finance'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="px-5 py-2 rounded-lg text-sm font-medium capitalize transition-all"
                style={{ background: activeTab === tab ? 'white' : 'transparent', color: activeTab === tab ? BRAND : '#6b7280', boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          {activeTab === 'overview' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">City:</span>
              <select value={selectedCity} onChange={e => { const c = e.target.value; setSelectedCity(c); localStorage.setItem('selectedCity', c); if (c !== 'All Cities' && companyCity && c !== companyCity) setShowCityPopup(true); }}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 bg-white focus:outline-none">
                {INDIAN_CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3">
                  {statCards.map((card, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: card.bg, color: card.color }}>
                          {card.icon}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: card.bg, color: card.color }}>FY 26-27</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-800 mb-1">{card.value}</p>
                      <p className="text-xs text-gray-400">{card.label}</p>
                      <p className="text-xs text-gray-300 mt-1">{card.sub}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-5 gap-4">
                  <div className="col-span-3 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-800">Expense Overview</p>
                      <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 cursor-pointer">
                        FY 2026-27 <span className="ml-1">▾</span>
                      </div>
                    </div>
                    {monthlyData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={monthlyData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.15}/>
                              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dy={8} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => v + 'L'} width={35} />
                          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }} />
                          <Area type="monotone" dataKey="spend" stroke="#3b82f6" strokeWidth={2.5} fill="url(#grad1)"
                            dot={false} activeDot={{ r: 5, fill: '#3b82f6', stroke: 'white', strokeWidth: 2 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-56 text-gray-300 text-sm">No data for selected city</div>
                    )}
                  </div>

                  <div className="col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                    <p className="text-sm font-semibold text-gray-800 mb-1">By Service</p>
                    <p className="text-xs text-gray-400 mb-5">Share of {formatLakh(totalSpend)}</p>
                    <div className="space-y-4">
                      {serviceSpend.length > 0 ? serviceSpend.map((s, i) => {
                        const pct = totalServiceSpend > 0 ? (parseFloat(s.spend) / totalServiceSpend * 100) : 0;
                        return (
                          <div key={s.service}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: SERVICE_COLORS[i]}}></span>
                                <p className="text-xs text-gray-600">{s.service === 'Corporate Gifting' ? 'Gifting' : s.service}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-gray-700">{formatLakh(parseFloat(s.spend||0))}</p>
                                <p className="text-xs text-gray-400 w-8 text-right">{pct.toFixed(0)}%</p>
                              </div>
                            </div>
                            <div className="h-1.5 rounded-full bg-gray-100">
                              <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: pct + '%', backgroundColor: SERVICE_COLORS[i] }}></div>
                            </div>
                          </div>
                        );
                      }) : (
                        <div className="text-xs text-gray-300 text-center pt-8">No data for selected city</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              cityMatches ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">Service-wise request analytics and reports.</p>
                  <div className="grid grid-cols-2 gap-4">
                    {SERVICES.map((svc) => {
                      const svcData = serviceSpend.find(s => s.service === svc.key);
                      const thisReq = svcData?.thisMonthRequests || 0;
                      const lastReq = svcData?.lastMonthRequests || 0;
                      const thisYearReq = svcData?.thisYearRequests || 0;
                      const lastYearReq = svcData?.lastYearRequests || 0;
                      const reqDiff = lastReq > 0 ? ((thisReq - lastReq) / lastReq * 100) : null;
                      const yearReqDiff = lastYearReq > 0 ? ((thisYearReq - lastYearReq) / lastYearReq * 100) : null;

                      return (
                        <div key={svc.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: svc.color + '15', color: svc.color }}>
                                {svc.icon}
                              </div>
                              <p className="font-semibold text-gray-800">{svc.label}</p>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: svc.color + '15', color: svc.color }}>
                              {svcData?.requests || 0} total
                            </span>
                          </div>

                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">This Month vs Last Month</p>
                          <div className="mb-4">
                            <div className="bg-gray-50 rounded-xl p-3">
                              <p className="text-xs text-gray-400 mb-2">Requests</p>
                              <div className="flex items-end justify-between">
                                <div>
                                  <p className="text-xs text-gray-400">{thisMonth}</p>
                                  <p className="text-base font-bold text-gray-800">{thisReq}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-400">{lastMonth}</p>
                                  <p className="text-sm font-medium text-gray-500">{lastReq}</p>
                                </div>
                              </div>
                              {reqDiff !== null ? (
                                <div className="mt-2 flex items-center gap-1">
                                  <span className={`text-xs font-semibold ${reqDiff >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {reqDiff >= 0 ? '▲' : '▼'} {Math.abs(reqDiff).toFixed(1)}%
                                  </span>
                                  <span className="text-xs text-gray-400">vs {lastMonth}</span>
                                </div>
                              ) : <p className="text-xs text-gray-300 mt-2">No {lastMonth} data</p>}
                            </div>
                          </div>

                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">This Year vs Last Year</p>
                          <div className="mb-3">
                            <div className="rounded-xl p-3" style={{ backgroundColor: svc.color + '08', border: `1px solid ${svc.color}20` }}>
                              <p className="text-xs text-gray-400 mb-2">Requests</p>
                              <div className="flex items-end justify-between">
                                <div>
                                  <p className="text-xs text-gray-400">{thisYear}</p>
                                  <p className="text-base font-bold text-gray-800">{thisYearReq}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-400">{lastYear}</p>
                                  <p className="text-sm font-medium text-gray-500">{lastYearReq}</p>
                                </div>
                              </div>
                              {yearReqDiff !== null ? (
                                <div className="mt-2 flex items-center gap-1">
                                  <span className={`text-xs font-semibold ${yearReqDiff >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {yearReqDiff >= 0 ? '▲' : '▼'} {Math.abs(yearReqDiff).toFixed(1)}%
                                  </span>
                                  <span className="text-xs text-gray-400">vs {lastYear}</span>
                                </div>
                              ) : <p className="text-xs text-gray-300 mt-2">No {lastYear} data</p>}
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-400 mt-1 pt-3 border-t border-gray-100">
                            <span>Total requests: <span className="font-semibold text-gray-600">{svcData?.requests || 0}</span></span>
                            <a href={'/client/reports?tab=' + reportTabMap[svc.key]} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-white" style={{ backgroundColor: svc.color }}
                              onClick={e => e.stopPropagation()}>
                              <MdDownload size={12}/> Report
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64">
                  <p className="text-lg font-semibold text-gray-300">No data for selected city</p>
                  <p className="text-sm mt-1 text-gray-300">Select "All Cities" or your company city to view reports</p>
                </div>
              )
            )}

            {activeTab === 'finance' && (
              cityMatches ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">Service-wise spend analytics.</p>
                  <div className="grid grid-cols-2 gap-4">
                    {SERVICES.map((svc) => {
                      const svcData = serviceSpend.find(s => s.service === svc.key);
                      const thisSpend = parseFloat(svcData?.thisMonthSpend || 0);
                      const lastSpend = parseFloat(svcData?.lastMonthSpend || 0);
                      const thisYearSpend = parseFloat(svcData?.thisYearSpend || 0);
                      const lastYearSpend = parseFloat(svcData?.lastYearSpend || 0);
                      const spendDiff = lastSpend > 0 ? ((thisSpend - lastSpend) / lastSpend * 100) : null;
                      const yearSpendDiff = lastYearSpend > 0 ? ((thisYearSpend - lastYearSpend) / lastYearSpend * 100) : null;

                      return (
                        <div key={svc.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: svc.color + '15', color: svc.color }}>
                                {svc.icon}
                              </div>
                              <p className="font-semibold text-gray-800">{svc.label}</p>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: svc.color + '15', color: svc.color }}>
                              {formatLakh(parseFloat(svcData?.spend || 0))} total
                            </span>
                          </div>

                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">This Month vs Last Month</p>
                          <div className="mb-4">
                            <div className="bg-gray-50 rounded-xl p-3">
                              <p className="text-xs text-gray-400 mb-2">Spend</p>
                              <div className="flex items-end justify-between">
                                <div>
                                  <p className="text-xs text-gray-400">{thisMonth}</p>
                                  <p className="text-base font-bold text-gray-800">{formatLakh(thisSpend)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-400">{lastMonth}</p>
                                  <p className="text-sm font-medium text-gray-500">{formatLakh(lastSpend)}</p>
                                </div>
                              </div>
                              {spendDiff !== null ? (
                                <div className="mt-2 flex items-center gap-1">
                                  <span className={`text-xs font-semibold ${spendDiff >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                                    {spendDiff >= 0 ? '▲' : '▼'} {Math.abs(spendDiff).toFixed(1)}%
                                  </span>
                                  <span className="text-xs text-gray-400">vs {lastMonth}</span>
                                </div>
                              ) : <p className="text-xs text-gray-300 mt-2">No {lastMonth} data</p>}
                            </div>
                          </div>

                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">This Year vs Last Year</p>
                          <div className="mb-3">
                            <div className="rounded-xl p-3" style={{ backgroundColor: svc.color + '08', border: `1px solid ${svc.color}20` }}>
                              <p className="text-xs text-gray-400 mb-2">Spend</p>
                              <div className="flex items-end justify-between">
                                <div>
                                  <p className="text-xs text-gray-400">{thisYear}</p>
                                  <p className="text-base font-bold text-gray-800">{formatLakh(thisYearSpend)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-400">{lastYear}</p>
                                  <p className="text-sm font-medium text-gray-500">{formatLakh(lastYearSpend)}</p>
                                </div>
                              </div>
                              {yearSpendDiff !== null ? (
                                <div className="mt-2 flex items-center gap-1">
                                  <span className={`text-xs font-semibold ${yearSpendDiff >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                                    {yearSpendDiff >= 0 ? '▲' : '▼'} {Math.abs(yearSpendDiff).toFixed(1)}%
                                  </span>
                                  <span className="text-xs text-gray-400">vs {lastYear}</span>
                                </div>
                              ) : <p className="text-xs text-gray-300 mt-2">No {lastYear} data</p>}
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-400 mt-1 pt-3 border-t border-gray-100">
                            <span>Total spend: <span className="font-semibold text-gray-600">{formatLakh(parseFloat(svcData?.spend || 0))}</span></span>
                            <span>Total requests: <span className="font-semibold text-gray-600">{svcData?.requests || 0}</span></span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64">
                  <p className="text-lg font-semibold text-gray-300">No data for selected city</p>
                  <p className="text-sm mt-1 text-gray-300">Select "All Cities" or your company city to view finance</p>
                </div>
              )
            )}
          </>
        )}
      </div>
      {showCityPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#068BC9" strokeWidth="2"/><path d="M12 8v4m0 4h.01" stroke="#068BC9" strokeWidth="2" strokeLinecap="round"/></svg>
            </div>
            <h2 className="text-lg font-bold text-gray-800 mb-2">{selectedCity} is not available</h2>
            <p className="text-sm text-gray-500 mb-6">{companyName || 'Your company'} does not operate in {selectedCity}. Please select a valid city.</p>
            <button onClick={() => { setSelectedCity(companyCity); localStorage.setItem('selectedCity', companyCity); setShowCityPopup(false); }}
              className="px-6 py-2 rounded-xl text-white text-sm font-medium" style={{ backgroundColor: '#068BC9' }}>
              Got it
            </button>
          </div>
        </div>
      )}
    </ClientLayout>
  );
}